import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Loader2, MailCheck } from "lucide-react";

/** Page for users who never received the verify email or whose link
 * expired. Always shows the same success message regardless of whether
 * the email is registered. */
export default function ResendVerification() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const t = {
    title: isAr ? "إعادة إرسال رابط التفعيل" : "Resend verification link",
    lead: isAr
      ? "أدخل بريدك الإلكتروني وسنرسل لك رابطًا جديدًا للتفعيل."
      : "Enter your email and we'll send you a fresh activation link.",
    email: isAr ? "البريد الإلكتروني" : "Email",
    submit: isAr ? "إرسال الرابط" : "Send link",
    sentTitle: isAr ? "تحقّق من بريدك" : "Check your inbox",
    sentBody: isAr
      ? "إذا كان البريد مسجّلاً ولم يكن مفعّلاً بعد، فستجد رابطاً جديداً خلال دقائق."
      : "If that email is registered and not yet verified, a fresh link is on its way.",
    backLogin: isAr ? "العودة لتسجيل الدخول" : "Back to sign-in",
    err: isAr ? "حدث خطأ، حاول مرة أخرى." : "Something went wrong.",
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/resend-verification", { email });
      setSent(true);
    } catch {
      toast.error(t.err);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="resend-verification-sent" className="max-w-md mx-auto px-6 py-32 text-center">
        <MailCheck className="w-14 h-14 mx-auto text-emerald-600" />
        <h1 className="font-display text-3xl mt-6 tracking-tight">{t.sentTitle}</h1>
        <p className="mt-3 text-slate-600 leading-relaxed">{t.sentBody}</p>
        <Link to="/login" className="inline-block mt-8 text-sm text-slate-700 underline underline-offset-4">
          {t.backLogin}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="resend-verification-page" className="max-w-md mx-auto px-6 py-24">
      <h1 className="font-display text-4xl tracking-tight">{t.title}</h1>
      <p className="mt-3 text-slate-600">{t.lead}</p>

      <form onSubmit={onSubmit} className="mt-12 grid gap-5" data-testid="resend-verification-form">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.email}</Label>
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="resend-verification-email"
            className="mt-2 rounded-sm h-11"
            dir="auto"
          />
        </div>
        <Button
          type="submit"
          disabled={busy || !email}
          data-testid="resend-verification-submit"
          className="rounded-sm h-12 bg-slate-900 hover:bg-slate-800 text-white"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t.submit}
        </Button>
        <Link to="/login" className="text-sm text-slate-500 underline underline-offset-4 text-center">
          {t.backLogin}
        </Link>
      </form>
    </div>
  );
}
